import {useMemo} from 'react';

import {useAppQuery} from '../../../../libs/shared/core/src/services/query/useAppQuery';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {CommentModel} from '../../../models/api/commentModel';
import {teammateQueries} from '../../../queries/teammateQueries';
import {AppState} from '../../../store/appState';
import {getConversations} from '../../../store/data/dataSelectors';
import {makeGetMentionableAndCommentAliases} from '../../../store/data/teammates/mentionTeammatesSelectors';
import {getIsInSandbox} from '../../../store/data/teams/teamsSelectors';

export function useMentionableAliases(conversationId: number, comment?: CommentModel | null) {
  const getIsSandboxConversation = (state: AppState) => {
    const conversations = getConversations(state);
    const isInSandbox = getIsInSandbox(state);
    const conversation = conversations[conversationId];
    return conversation ? isInSandbox(conversation) : false;
  };

  // From Redux
  const isSandboxConversation = useAppSelector(getIsSandboxConversation);
  const getMentionableAndCommentAliases = useMemo(
    () => makeGetMentionableAndCommentAliases(() => comment),
    [comment],
  );

  const mentionableAliases = useAppSelector(getMentionableAndCommentAliases);

  // From react-query
  const sandboxGuests = useAppQuery({
    ...teammateQueries.sandboxGuests(),
    enabled: isSandboxConversation,
  });

  return useMemo(() => {
    // If the conversation is in a sandbox inbox, add the sandbox guests.
    if (isSandboxConversation && sandboxGuests.data) {
      const sandboxGuestAliases = sandboxGuests.data.map((t) => t.alias);

      return new Set([...mentionableAliases, ...sandboxGuestAliases]);
    }

    // Otherwise, include only regular teammates.
    return mentionableAliases;
  }, [isSandboxConversation, mentionableAliases, sandboxGuests.data]);
}
